import { useTranslation } from "react-i18next";
import { ChevronRight, ArrowRight } from "lucide-react";
import { Button, Card, CardContent } from "@tabipla/ui";
import { apps } from "@/config/apps";

export function HomePage() {
  const { t } = useTranslation();

  // Extract icons for use in JSX
  const TabittoIcon = apps[0].icon;
  const WarittoIcon = apps[1].icon;
  const MekkeIcon = apps[2].icon;

  return (
    <div className="animate-fade-in">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-background via-background to-muted/30 py-16 sm:py-24">
        <div className="mx-auto max-w-6xl px-4">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            {/* Left: Text Content */}
            <div className="text-center lg:text-left">
              {/* App Icons */}
              <div className="mb-6 flex justify-center gap-3 lg:justify-start">
                {apps.map((app) => (
                  <div
                    key={app.id}
                    className={`flex h-14 w-14 items-center justify-center rounded-2xl ${app.colorClass} text-white shadow-lg transition-transform hover:scale-110`}
                  >
                    <app.icon className="h-7 w-7" />
                  </div>
                ))}
              </div>

              <h1 className="mb-4 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
                {t("home.hero.title")}
              </h1>
              <p className="mx-auto mb-8 max-w-xl text-lg text-muted-foreground sm:text-xl lg:mx-0">
                {t("home.hero.subtitle")}
              </p>

              <div className="flex flex-wrap justify-center gap-3 lg:justify-start">
                {apps.map((app) => (
                  <a key={app.id} href={app.url} target="_blank" rel="noopener noreferrer">
                    <Button
                      variant="outline"
                      className={`${app.textColorClass} border-current hover:${app.lightBgClass}`}
                    >
                      <app.icon className="mr-2 h-4 w-4" />
                      {app.name}
                    </Button>
                  </a>
                ))}
              </div>
            </div>

            {/* Right: Illustration */}
            <div className="hidden lg:block">
              <img
                src="/illustrations/travelers.svg"
                alt="Travelers illustration"
                className="mx-auto w-full max-w-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Apps Section */}
      <section className="py-20">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-12 text-center">
            <h2 className="mb-3 text-3xl font-bold sm:text-4xl">{t("home.apps.title")}</h2>
            <p className="text-lg text-muted-foreground">{t("home.apps.subtitle")}</p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {apps.map((app) => (
              <Card key={app.id} className="group overflow-hidden transition-all hover:shadow-lg">
                <CardContent className="p-0">
                  {/* Header with color and illustration */}
                  <div
                    className={`${app.colorClass} relative overflow-hidden px-6 py-8 text-white`}
                  >
                    {/* Background illustration */}
                    <img
                      src={app.illustration}
                      alt=""
                      className="absolute right-4 top-1/2 h-24 w-24 -translate-y-1/2 object-contain opacity-25"
                    />
                    <div className="relative flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20">
                        <app.icon className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold">{app.name}</h3>
                        <p className="text-sm text-white/80">{t(`apps.${app.id}.tagline`)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div>
                      <p className="mb-4 text-muted-foreground">
                        {t(`apps.${app.id}.description`)}
                      </p>

                      <ul className="mb-6 space-y-3">
                        {(t(`apps.${app.id}.features`, { returnObjects: true }) as string[]).map(
                          (feature, i) => (
                            <li key={i} className="flex items-center gap-3 text-sm">
                              <div
                                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${app.lightBgClass}`}
                              >
                                <div className={`h-2 w-2 rounded-full ${app.colorClass}`} />
                              </div>
                              <span className="font-medium">{feature}</span>
                            </li>
                          )
                        )}
                      </ul>

                      <a href={app.url} target="_blank" rel="noopener noreferrer">
                        <Button className={`w-full ${app.colorClass} text-white hover:opacity-90`}>
                          {t("home.apps.tryNow")}
                          <ChevronRight className="ml-1 h-4 w-4" />
                        </Button>
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="bg-blue-50 py-20">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-12 text-center">
            <h2 className="mb-3 text-3xl font-bold sm:text-4xl">{t("home.integration.title")}</h2>
            <p className="text-lg text-muted-foreground">{t("home.integration.subtitle")}</p>
          </div>

          {/* Desktop: Horizontal flow with illustrations */}
          <div className="hidden md:block">
            <div className="grid grid-cols-5 items-center gap-4">
              {/* Step 1: Tabitto */}
              <div className="text-center">
                <div className="mb-4 flex justify-center">
                  <img
                    src="/illustrations/travel-plans.svg"
                    alt="Plan trip"
                    className="h-32 w-32 object-contain"
                  />
                </div>
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-blue-500 text-white">
                  <TabittoIcon className="h-6 w-6" />
                </div>
                <p className="mt-2 text-xs text-muted-foreground">Step 1</p>
                <p className="font-medium">{t("home.integration.step1")}</p>
              </div>

              <div className="flex justify-center">
                <ArrowRight className="h-8 w-8 text-muted-foreground" />
              </div>

              {/* Step 2: Mekke */}
              <div className="text-center">
                <div className="mb-4 flex justify-center">
                  <img
                    src="/illustrations/adventure-map.svg"
                    alt="Record spots"
                    className="h-32 w-32 object-contain"
                  />
                </div>
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-500 text-white">
                  <MekkeIcon className="h-6 w-6" />
                </div>
                <p className="mt-2 text-xs text-muted-foreground">Step 2</p>
                <p className="font-medium">{t("home.integration.step2")}</p>
              </div>

              <div className="flex justify-center">
                <ArrowRight className="h-8 w-8 text-muted-foreground" />
              </div>

              {/* Step 3: Waritto */}
              <div className="text-center">
                <div className="mb-4 flex justify-center">
                  <img
                    src="/illustrations/personal-finance.svg"
                    alt="Split expenses"
                    className="h-32 w-32 object-contain"
                  />
                </div>
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500 text-white">
                  <WarittoIcon className="h-6 w-6" />
                </div>
                <p className="mt-2 text-xs text-muted-foreground">Step 3</p>
                <p className="font-medium">{t("home.integration.step3")}</p>
              </div>
            </div>
          </div>

          {/* Mobile: Vertical flow */}
          <div className="flex flex-col items-center gap-4 md:hidden">
            {/* Step 1: Tabitto */}
            <div className="relative flex w-full max-w-sm items-center gap-4 overflow-hidden rounded-2xl bg-background p-4 shadow-sm">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-500 text-white">
                <TabittoIcon className="h-6 w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground">Step 1</p>
                <p className="font-medium">{t("home.integration.step1")}</p>
              </div>
              <img
                src="/illustrations/travel-plans.svg"
                alt=""
                className="absolute right-2 top-1/2 h-16 w-16 -translate-y-1/2 object-contain opacity-70"
              />
            </div>

            <div className="h-4 w-px bg-border" />

            {/* Step 2: Mekke */}
            <div className="relative flex w-full max-w-sm items-center gap-4 overflow-hidden rounded-2xl bg-background p-4 shadow-sm">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-indigo-500 text-white">
                <MekkeIcon className="h-6 w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground">Step 2</p>
                <p className="font-medium">{t("home.integration.step2")}</p>
              </div>
              <img
                src="/illustrations/adventure-map.svg"
                alt=""
                className="absolute right-2 top-1/2 h-16 w-16 -translate-y-1/2 object-contain opacity-70"
              />
            </div>

            <div className="h-4 w-px bg-border" />

            {/* Step 3: Waritto */}
            <div className="relative flex w-full max-w-sm items-center gap-4 overflow-hidden rounded-2xl bg-background p-4 shadow-sm">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-500 text-white">
                <WarittoIcon className="h-6 w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground">Step 3</p>
                <p className="font-medium">{t("home.integration.step3")}</p>
              </div>
              <img
                src="/illustrations/personal-finance.svg"
                alt=""
                className="absolute right-2 top-1/2 h-16 w-16 -translate-y-1/2 object-contain opacity-70"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="mx-auto max-w-6xl px-4 text-center">
          <h2 className="mb-3 text-3xl font-bold sm:text-4xl">{t("home.cta.title")}</h2>
          <p className="mb-8 text-lg text-muted-foreground">{t("home.cta.subtitle")}</p>

          <div className="flex flex-wrap justify-center gap-4">
            {apps.map((app) => (
              <a key={app.id} href={app.url} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className={`${app.colorClass} text-white hover:opacity-90`}>
                  <app.icon className="mr-2 h-5 w-5" />
                  {app.name}
                  <ChevronRight className="ml-1 h-5 w-5" />
                </Button>
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
